import React from 'react';
import { Youtube, AlertCircle, Check } from 'lucide-react';
import { ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * VideoEmbedEditor - Editor component for video-embed block (YouTube only)
 */
const VideoEmbedEditor = ({ block, onChange, youtubeVideos = [], theme, hideStylePanels }) => {
  const { content } = block;

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  // Parse URL to check if it's valid YouTube URL
  const getVideoInfo = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? { id: match[1] } : null;
  };

  // Get YouTube thumbnail URL
  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  const videoInfo = getVideoInfo(content.url);
  const isValidUrl = content.url && videoInfo;

  // Check if a video from the list is currently selected
  const isVideoSelected = (video) => {
    if (!content.url) return false;
    const currentVideoInfo = getVideoInfo(content.url);
    return currentVideoInfo && currentVideoInfo.id === video.videoId;
  };

  return (
    <div className="space-y-4">
      {/* Select from your videos */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Select from your videos
        </label>
        {youtubeVideos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {youtubeVideos.map((video) => (
              <button
                key={video.id}
                onClick={() => handleChange('url', video.url)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                  isVideoSelected(video)
                    ? 'border-[#749555] ring-2 ring-[#749555]/30'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                <img
                  src={video.thumbnailUrl || getYouTubeThumbnail(video.videoId)}
                  alt={video.title}
                  className="w-full aspect-video object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-end p-1.5">
                  <span className="text-[10px] text-white truncate leading-tight">
                    {video.title}
                  </span>
                </div>
                {isVideoSelected(video) && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-[#749555] rounded-full flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-[#1a1a2e] border border-gray-700 rounded-lg">
            <Youtube size={24} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No videos in your profile yet</p>
            <p className="text-gray-600 text-xs mt-1">
              Add videos via the YouTube Gallery block
            </p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-700" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-[#16162a] text-gray-500">Or paste a URL</span>
        </div>
      </div>

      {/* Video URL */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          YouTube URL
        </label>
        <input
          type="text"
          value={content.url || ''}
          onChange={(e) => handleChange('url', e.target.value)}
          placeholder="https://youtube.com/watch?v=..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
        {content.url && !isValidUrl && (
          <p className="text-amber-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle size={14} />
            Enter a valid YouTube URL
          </p>
        )}
        {isValidUrl && (
          <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
            <Youtube size={14} />
            YouTube video detected
          </p>
        )}
      </div>

      {/* Video Preview */}
      {isValidUrl && (
        <div className="rounded-lg overflow-hidden border border-gray-700">
          <div className="aspect-video bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${videoInfo.id}`}
              title="Video preview"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Caption */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Caption (optional)
        </label>
        <input
          type="text"
          value={content.caption || ''}
          onChange={(e) => handleChange('caption', e.target.value)}
          placeholder="Video description..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Style Panel */}
      {!hideStylePanels && (
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['video-embed']}
        theme={theme || {}}
        blockType="video-embed"
      />
      )}
    </div>
  );
};

export default VideoEmbedEditor;
